import React, { useState, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowUpDown, Grid3x3, List } from "lucide-react";

import { ResourceCard } from "@/modules/hub/features/ResourceLibrary/components/ResourceCard/ResourceCard";
import { ResourceListItem } from "@/modules/hub/features/ResourceLibrary/components/ResourceListItem/ResourceListItem";
import { FilterSidebar } from "@/modules/hub/features/ResourceLibrary/components/FilterSidebar/FilterSidebar/FilterSidebar";
import { FeaturedResources } from "@/modules/hub/features/ResourceLibrary/components/FeaturedResources/FeaturedResources";
import ActiveFiltersHandler from "@/modules/hub/features/ResourceLibrary/components/ActiveFiltersHandler/ActiveFiltersHandler";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import "./ResourceLibrary.css";
import { useResourcesLibrary } from "../../hooks/useResourceLibrary";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileFilterToggleButton from "../MobileFilterToggleButton/MobileFilterToggleButton";
import { ResourcesLibrarySkeleton } from "@/modules/hub/features/ResourceLibrary/components/ResourceLibrary/ResourceLibrarySkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trackResourceLibraryPageViewed } from "../../analytics/resourceLibraryAnalytics";

export function ResourcesLibrary() {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackResourceLibraryPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const {
    form,
    resources,
    hasMore,
    totalCount,
    loading,
    isInitialLoading,
    sidebarMetadata,
    isMetadataLoading,
    fetchMore,
    handleClearFilters,
    sortOption,
    setSortOption,
    sortOptions,
    viewMode,
    handleViewModeChange,
    handleViewAllFeatured,
    getResourceTypeIds,
  } = useResourcesLibrary(["Podcast", "News"]);

  const isMobile = useIsMobile();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  if (isInitialLoading) {
    return <ResourcesLibrarySkeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <MobileFilterToggleButton
          onClick={() => setIsFilterSidebarOpen(true)}
        />
      }
      subtitle="Access and discover learning materials, documents, videos, and more"
      title="Resource Library"
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div
          className={
            isMobile
              ? undefined
              : "w-80 shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto"
          }
        >
          <FilterSidebar
            filters={sidebarMetadata}
            form={form}
            isLoading={isMetadataLoading}
            isOpen={isFilterSidebarOpen}
            onOpenChange={setIsFilterSidebarOpen}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Featured Resources Section - Only show when no active filters */}
          {!form.watch("searchTerm") &&
            !form.watch("isFavoriteOnly") &&
            !form.watch("isFeaturedOnly") &&
            form.watch("trades").length === 0 &&
            form.watch("contentTypes").length === 0 &&
            form.watch("categories").length === 0 &&
            form.watch("businessRoles").length === 0 && (
              <FeaturedResources
                onViewAllFeatured={handleViewAllFeatured}
                resourceTypeIds={getResourceTypeIds()}
              />
            )}

          <ActiveFiltersHandler form={form} onClearAll={handleClearFilters} />

          {/* Sort Controls and View Toggle */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {totalCount > 0 && `${totalCount} resources found`}
            </div>
            <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                <Select
                  onValueChange={(value) => setSortOption(value as any)}
                  value={sortOption}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-md p-1">
                <Button
                  className="h-7 w-7 p-0"
                  onClick={() => handleViewModeChange("card")}
                  size="sm"
                  title="Card View"
                  variant={viewMode === "card" ? "secondary" : "ghost"}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  className="h-7 w-7 p-0"
                  onClick={() => handleViewModeChange("list")}
                  size="sm"
                  title="List View"
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <InfiniteScroll
            dataLength={resources.length}
            endMessage={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  No more resources to load
                </p>
              </div>
            }
            hasMore={hasMore}
            loader={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  Loading more...
                </p>
              </div>
            }
            next={fetchMore}
            style={{ overflow: "visible" }}
          >
            {loading && resources.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  Searching...
                </p>
              </div>
            ) : resources.length > 0 ? (
              viewMode === "card" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {resources.map((resource) => (
                    <div className="fade-in" key={resource.id}>
                      <ResourceCard resource={resource} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {resources.map((resource) => (
                    <div className="fade-in" key={resource.id}>
                      <ResourceListItem resource={resource} />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  No resources found matching your criteria.
                </p>
                <button
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </MainPageWrapper>
  );
}
